/** Choose (/choose): the template queue as a table (search, sort, bulk choose) above a Tinder-style template chooser
 * over the `template` work queue.
 * URL: exclude (← next history), clip (a pinned clip, e.g. from the queue table, the Clip page or after Undo). */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";
import { fmtMs } from "../../lib/frames";
import { isAgentMode } from "../../lib/agent";
import { ClipPreview } from "../clip/ClipPreview";
import { Loading, WriteError, when } from "../common";
import { KeyButton, useKeyBindings, type KeyBinding } from "../keys";
import type { FrameController } from "../player/controller";
import { CanvasOverlay, ClipLanesPanel, useClipLanes } from "./ClipLanes";
import { PaletteCard } from "./PaletteCard";
import { QueueTable } from "./QueueTable";
import { TemplateCard } from "./TemplateCard";
import { useChooser } from "./useChooser";

function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

function secondsLeft(untilMs: number, now: number): number {
  return Math.max(0, Math.ceil((untilMs - now) / 1000));
}

export function ChooseView() {
  const agent = isAgentMode();
  const c = useChooser();
  const { s, actions: a } = c;
  const now = useNow(s.undo != null || c.work.data?.item?.lease_until != null);
  const clip = c.clip.data;
  const ready = c.phase === "ready";
  const undoLeft = s.undo ? secondsLeft(s.undo.until, now) : 0;
  // the clip preview's player: this clip's lanes seek it and follow its playhead
  const [ctrl, setCtrl] = useState<FrameController | null>(null);
  const [overlay, setOverlay] = useState(false);
  const lanes = useClipLanes(ready ? c.clipId : null);

  const b: Record<string, KeyBinding> = {
    next: { key: "ArrowLeft", label: "← Next clip", testId: "next-clip", run: a.next, disabled: !ready || s.submitting },
    choose: { key: "ArrowRight", label: "Choose →", testId: "choose-focused", run: a.chooseFocused,
      disabled: !ready || c.opts.length === 0 },
    ok: { key: "Enter", label: "OK", testId: "ok", run: () => void a.submit(), disabled: !ready || s.submitting },
    skip: { key: "s", label: "Skip clip", testId: "skip-clip", run: () => void a.skip(), disabled: !ready || !clip },
    undo: { key: "u", label: s.undo ? `Undo (${undoLeft} s)` : "Undo", testId: "undo", run: () => void a.undo(),
      disabled: !s.undo },
    up: { key: "ArrowUp", label: "Prev template", testId: "prev-template", run: () => a.focusBy(-1),
      disabled: !ready || c.focus <= 0 },
    down: { key: "ArrowDown", label: "Next template", testId: "next-template", run: () => a.focusBy(1),
      disabled: !ready || c.focus >= c.opts.length - 1 },
  };
  useKeyBindings(Object.values(b));

  const lease = c.work.data?.item?.lease_until;
  const titleOf = (id: string) => c.opts.find((o) => o.template.id === id)?.template.title ?? id;

  return (
    <section className="ws8-page" data-testid="choose-page">
      <PageHeader title="Choose templates" md={c.clipId != null ? { page: "clip", params: { id: c.clipId } } : undefined}>
        <KeyButton b={b.undo as KeyBinding} />
      </PageHeader>
      <p className="ws8-muted" data-testid="queue-info">
        {s.pinned != null ? `Clip #${s.pinned} opened directly (no queue lease). ` : ""}
        {c.work.data && s.pinned == null ? `${c.work.data.remaining} clip${c.work.data.remaining === 1 ? "" : "s"} need a template. ` : ""}
        {lease && s.pinned == null ? `Lease until ${when(lease)} (${secondsLeft(lease * 1000, now)} s left). ` : ""}
        Session: {s.log.queued.length} renders queued, {s.log.skipped.length} skipped, {s.log.passed.length} passed.
      </p>
      <QueueTable current={c.clipId} onPin={a.pin} onChanged={c.reload} />
      <WriteError error={c.error} onReload={c.reload} />
      {c.phase === "loading" && <Loading what="the next clip" />}
      {c.phase === "error" && <WriteError error={c.work.error} onReload={c.reload} testId="queue-error" />}
      {c.phase === "none_left" && (
        <div className="ws8-panel" data-testid="none-left">
          <h2 className="ws8-h2">No clips left</h2>
          <p>
            Every clip that needs a template has been handled or passed. This session: {s.log.queued.length} render
            {s.log.queued.length === 1 ? "" : "s"} queued{s.log.queued.length ? ` (${s.log.queued.map((r) => `#${r}`).join(", ")})` : ""},
            {" "}{s.log.skipped.length} clip{s.log.skipped.length === 1 ? "" : "s"} skipped, {s.log.passed.length} passed.
          </p>
          <div className="ws8-row">
            <button type="button" data-testid="restart" disabled={s.exclude.length === 0} onClick={a.restart}>
              Start over (bring back passed clips)
            </button>
            <Link className="button" to="/board/renders">Renders board</Link>
          </div>
        </div>
      )}
      {ready && c.clipId != null && (
        <div className="ws8-choose">
          <div className="ws8-choose-left">
          <div className="ws8-panel" data-testid="choose-clip">
            {clip ? (
              <>
                <h2 className="ws8-h2">Clip #{clip.id}{clip.label ? `: ${clip.label}` : ""}</h2>
                {clip.kind === "song" ? (
                  <p className="ws8-muted" data-testid="choose-song-clip">
                    <span className="ws8-badge">song clip</span> no music video · song {fmtMs(clip.t_song_in_ms)}–{fmtMs(clip.t_song_out_ms)}
                    {" "}({fmtMs(clip.dur_ms)} with the fade) · {clip.template_state.replace("_", " ")}
                    {clip.song_id != null && <>{" · "}<Link to={`/songs/${clip.song_id}`}>{clip.song_title ? `Song: ${clip.song_title}` : "Song"}</Link></>}
                    {" · "}<Link to={`/clips/${clip.id}`}>Clip page</Link>
                  </p>
                ) : (
                  <p className="ws8-muted">
                    MV {fmtMs(clip.t_mv_in_ms)}–{fmtMs(clip.t_mv_out_ms)} ({fmtMs(clip.dur_ms)}) · {clip.template_state.replace("_", " ")}
                    {" · "}<Link to={`/watch/${clip.video_id}?in=${clip.t_mv_in_ms}&out=${clip.t_mv_out_ms}&t=${clip.t_mv_in_ms}`}>Open in Watch</Link>
                    {" · "}<Link to={`/clips/${clip.id}`}>Clip page</Link>
                  </p>
                )}
                <ClipPreview key={clip.id} clip={clip} loop autoplay={!agent} onController={setCtrl}
                  overlay={overlay && ctrl && lanes.data ? <CanvasOverlay l={lanes.data} ctrl={ctrl} /> : undefined} />
              </>
            ) : c.clip.isError ? <WriteError error={c.clip.error} onReload={c.reload} testId="clip-error" /> : <Loading what="clip" />}
          </div>
          <ClipLanesPanel key={c.clipId} clipId={c.clipId} q={lanes} ctrl={clip ? ctrl : null} agent={agent}
            overlay={overlay} setOverlay={setOverlay} />
          </div>
          <div className="ws8-panel" data-testid="choose-templates">
            <div className="ws8-row" role="group" aria-label="Chooser actions">
              <KeyButton b={b.next as KeyBinding} />
              <KeyButton b={b.choose as KeyBinding} />
              <KeyButton b={b.ok as KeyBinding} className="ws8-primary" />
              <KeyButton b={b.skip as KeyBinding} />
              <KeyButton b={b.up as KeyBinding} />
              <KeyButton b={b.down as KeyBinding} />
            </div>
            <p data-testid="selection">
              {s.submitting ? "Queuing… " : ""}
              Selected: {s.selected.length ? s.selected.map(titleOf).join(", ") : "none (Enter chooses the highlighted template)"}
            </p>
            {c.opts.length > 0 && (
              <PaletteCard clipId={c.clipId} template={c.opts[c.focus]?.template ?? null} />
            )}
            {c.options.isPending && <Loading what="templates" />}
            {c.options.isError && <WriteError error={c.options.error} onReload={c.reload} testId="options-error" />}
            {c.options.isSuccess && c.opts.length === 0 && <p>No templates are available for this clip.</p>}
            <ol className="ws8-cards" aria-label="Template cards">
              {c.opts.map((o, i) => (
                <TemplateCard key={o.template.id} o={o} index={i} focused={i === c.focus}
                  selected={s.selected.includes(o.template.id)} agent={agent} clipId={c.clipId as number}
                  params={c.params[o.template.id] ?? {}} onParams={(p) => a.setParams(o.template.id, p)}
                  onToggle={() => a.toggle(o.template.id)} onFocus={() => a.focusAt(i)} />
              ))}
            </ol>
          </div>
        </div>
      )}
    </section>
  );
}
